import * as React from 'react';
import type {ItemsType} from '@packages/systems/core/records/CollectionStateType';
import {weakMemo} from '@packages/utilities/memo';

type ItemNamesById = {[itemId: string]: string};

const EMPTY_INDEX: ItemNamesById = {};

const DesignerCanvasItemsContext =
  React.createContext<ItemNamesById>(EMPTY_INDEX);

/**
 * Return the item names by item id dictionary from context.
 */
export function useDesignerExpansionItems(): ItemNamesById {
  return React.useContext(DesignerCanvasItemsContext);
}

/**
 * Provides DesignerCanvasItemsContext for the Designer renderer
 *
 * Handles deriving the dictionary of item names keyed by item id from the
 * `items` prop and ensures that the context value reference only changes if
 * the value has truly changed.
 */
export function DesignerCanvasItemsProvider({
  items,
  children,
}: React.PropsWithChildren<{items: ItemsType}>) {
  const [namesById, setNamesById] = React.useState(() => getNamesById(items));

  // update the dictionary as an effect
  React.useEffect(() => {
    const nextNamesById = getNamesById(items);
    if (!areEqual(namesById, nextNamesById)) {
      // only update the dictionary reference if it has changed
      setNamesById(nextNamesById);
    }
  }, [items, namesById]);

  // use a deferred value so that re-renders caused by context value changes
  // happen in the background and are interruptible
  const deferredNamesById = React.useDeferredValue(namesById);

  return (
    <DesignerCanvasItemsContext.Provider value={deferredNamesById}>
      {children}
    </DesignerCanvasItemsContext.Provider>
  );
}

/**
 * Derive a dictionary of item names keyed by item id from `items`.
 */
const getNamesById = weakMemo((items: ItemsType): ItemNamesById => {
  const namesById: ItemNamesById = {};

  items.forEach((item, itemId) => {
    const name = item.get('name');
    if (name && typeof name === 'string') {
      namesById[itemId] = name;
    }
  });

  return namesById;
});

/**
 * Check if two item name dictionaries are equal.
 */
function areEqual(indexA: ItemNamesById, indexB: ItemNamesById) {
  if (indexA === indexB) {
    return true;
  }
  const keysA = Object.keys(indexA);
  if (keysA.length !== Object.keys(indexB).length) {
    return false;
  }
  return keysA.every((key) => indexA[key] === indexB[key]);
}
